import React, { useContext } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FaConciergeBell, FaMapMarkerAlt, FaUtensils, FaWhatsapp } from 'react-icons/fa';
import { LanguageContext } from '../../../contexts/LanguageContext';
import { RESTAURANT_WHATSAPP_DISPLAY, RESTAURANT_WHATSAPP_URL } from '../../../constants/contact';
import { openReservationModal } from '../../../utils/openReservationModal';
import '../css/HomePage.css';
import background from '../../../assets/images/HomePageImage.png';

const HomePage = () => {
  const { t } = useContext(LanguageContext);
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 320], [0, -48]);

  const highlights = [
    { icon: FaUtensils, label: t('heroBadgeCuisine') },
    { icon: FaMapMarkerAlt, label: t('heroBadgeLocation') },
    { icon: FaConciergeBell, label: t('heroBadgeReservation') },
  ];

  return (
    <motion.section
      className="hero-section"
      style={{
        backgroundImage: `linear-gradient(120deg, rgba(15, 51, 38, 0.9), rgba(15, 51, 38, 0.52)), url(${background})`,
      }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: false, amount: 0.5 }}
      transition={{ duration: 1 }}
    >
      <div className="hero-backdrop" />

      <div className="hero-content">
        <motion.div
          className="hero-copy"
          style={{ y: parallaxY }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: false, amount: 0.5 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        >
          <span className="section-kicker hero-kicker">{t('heroEyebrow')}</span>

          <motion.h1
            className="hero-title"
            initial={{ y: -50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: false, amount: 0.5 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          >
            {t('welcome')} <span>{t('welcomeSecond')}</span>
          </motion.h1>

          <motion.p
            className="hero-subtitle"
            initial={{ y: 32, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: false, amount: 0.5 }}
            transition={{ duration: 0.9, ease: 'easeOut', delay: 0.18 }}
          >
            {t('heroText')}
          </motion.p>

          <motion.div
            className="hero-meta"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.4 }}
            transition={{ duration: 0.8, delay: 0.28 }}
          >
            {highlights.map(({ icon: Icon, label }) => (
              <span key={label} className="hero-meta-item">
                <Icon aria-hidden="true" />
                {label}
              </span>
            ))}
          </motion.div>

          <motion.div
            className="hero-underline"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: false, amount: 0.5 }}
            transition={{ delay: 0.45, duration: 0.85 }}
          />

          <div className="cta-wrapper">
            <motion.a
              href="#menu"
              className="cta-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: false, amount: 0.5 }}
              transition={{ delay: 0.62, duration: 0.7 }}
            >
              {t('viewMenu')}
            </motion.a>

            <motion.button
              type="button"
              className="cta-button reserve-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: false, amount: 0.5 }}
              transition={{ delay: 0.68, duration: 0.7 }}
              onClick={openReservationModal}
            >
              {t('reserveTable')}
            </motion.button>

            <motion.a
              href={RESTAURANT_WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button ghost-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: false, amount: 0.5 }}
              transition={{ delay: 0.74, duration: 0.7 }}
            >
              <FaWhatsapp aria-hidden="true" />
              {t('heroWhatsapp')}
            </motion.a>
          </div>

          <p className="hero-contact-note">
            {t('heroQuickNote')}{' '}
            <a href={RESTAURANT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              {RESTAURANT_WHATSAPP_DISPLAY}
            </a>
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default HomePage;
